import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import { apiService } from '../../services/apiService';
import BackgroundIcons from '../../components/BackgroundIcons';
import NotificationModal from '../../components/NotificationModal';

const WeatherRiskAdvisor = () => {
  const [location, setLocation] = useState('');
  const [weddingDate, setWeddingDate] = useState('');
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [serverStatus, setServerStatus] = useState('checking');
  const [apiStatus, setApiStatus] = useState(null);
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  const [quickChecklist, setQuickChecklist] = useState([]);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ show: false, title: '', message: '', type: '' });
  const [savedForecastId, setSavedForecastId] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    setIsVisible(true);
    checkServerConnection();
    loadSavedForecast();
  }, []);

  const loadSavedForecast = async () => {
    const userId = localStorage.getItem('userId');
    if (!userId || !isInitialLoad) return;
    
    try {
      const response = await apiService.weather.getSavedForecast();
      
      if (response.data.forecast) {
        const { location, wedding_date, forecast_data, ai_recommendations, vendor_suggestions, quick_checklist, id } = response.data.forecast;
        setLocation(location);
        setWeddingDate(wedding_date.split('T')[0]);
        setForecast({
          ...forecast_data,
          recommendations: ai_recommendations || [],
          vendorSuggestions: vendor_suggestions || [],
          checklist: quick_checklist || []
        });
        setAiRecommendations(ai_recommendations || []);
        setVendorSuggestions(vendor_suggestions || []);
        setQuickChecklist(quick_checklist || []);
        setSavedForecastId(id);
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.log('No saved forecast found');
    } finally {
      setIsInitialLoad(false);
    }
  };

  const checkServerConnection = async () => {
    try {
      console.log('Checking server connection...');
      const response = await fetch(`${API_ENDPOINTS.health}`);
      if (response.ok) {
        setServerStatus('connected');
        checkWeatherApiStatus();
      } else {
        setServerStatus('disconnected');
      }
    } catch (error) {
      console.error('Server connection failed:', error);
      setServerStatus('disconnected');
      setError('Cannot connect to server. Please make sure the server is running.');
    }
  };

  const checkWeatherApiStatus = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.weather}/status`);
      if (response.ok) {
        const data = await response.json();
        setApiStatus(data);
        console.log('Weather API status:', data);
      }
    } catch (error) {
      console.error('Weather API status check failed:', error);
      setApiStatus({
        weather_api_available: false,
        api_provider: 'OpenWeatherMap',
        status: 'API key not configured',
        message: 'Using simulated weather data'
      });
    }
  };

  const weatherIcons = {
    sunny: '☀️',
    partlyCloudy: '⛅',
    cloudy: '☁️',
    rainy: '🌧️',
    stormy: '⛈️',
    snowy: '❄️',
    windy: '💨'
  };

  const riskLevels = {
    low: { color: 'from-emerald-100 to-green-200', textColor: 'text-emerald-700', icon: '✅' },
    medium: { color: 'from-yellow-100 to-orange-200', textColor: 'text-orange-700', icon: '⚠️' },
    high: { color: 'from-red-100 to-pink-200', textColor: 'text-red-700', icon: '🚨' }
  };

  const searchWeather = async () => {
    if (!location || !weddingDate) {
      setError('Please enter both location and wedding date');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Always try to use real weather API first
      console.log('Attempting to use real weather API...');
      const response = await fetch(`${API_ENDPOINTS.weather}/forecast`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          location: location,
          date: weddingDate
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Real weather API successful:', data);
        setForecast({
          ...data.forecast,
          api_used: true,
          data_source: 'Real Weather API'
        });
        
        // Extract AI recommendations, vendor suggestions, and checklist
        if (data.forecast.ai_recommendations) {
          setAiRecommendations(data.forecast.ai_recommendations);
        }
        if (data.forecast.vendor_suggestions) {
          setVendorSuggestions(data.forecast.vendor_suggestions);
        }
        if (data.forecast.quick_checklist) {
          setQuickChecklist(data.forecast.quick_checklist);
        }
        
        // Mark as unsaved changes
        setHasUnsavedChanges(true);
        
        setLoading(false);
        return;
      } else {
        console.log('Weather API returned error status:', response.status);
        const errorData = await response.json().catch(() => ({}));
        console.log('Error details:', errorData);
      }
    } catch (apiError) {
      console.error('Weather API call failed:', apiError);
    }

    // Fallback to simulated data only if API fails
    console.log('Using simulated weather data as fallback...');
    try {
      setTimeout(() => {
        const sampleForecast = {
          location: location,
          date: weddingDate,
          temperature: {
            high: Math.floor(Math.random() * 20) + 65, // 65-85°F
            low: Math.floor(Math.random() * 15) + 45   // 45-60°F
          },
          conditions: ['sunny', 'partlyCloudy', 'cloudy', 'rainy'][Math.floor(Math.random() * 4)],
          humidity: Math.floor(Math.random() * 40) + 40, // 40-80%
          windSpeed: Math.floor(Math.random() * 15) + 5, // 5-20 mph
          precipitation: Math.floor(Math.random() * 60), // 0-60%
          riskLevel: ['low', 'medium', 'high'][Math.floor(Math.random() * 3)],
          api_used: false,
          data_source: 'Simulated Data',
          recommendations: [
            'Consider having a backup indoor option for the ceremony',
            'Provide light jackets or shawls for guests during evening reception',
            'Set up tent sides that can be deployed if needed',
            'Have umbrellas available for guests',
            'Consider moving cocktail hour indoors if rain develops'
          ],
          hourlyForecast: [
            { time: '12:00 AM - 3:00 AM', temp: 52, condition: 'partlyCloudy', precipitation: 5 },
            { time: '3:00 AM - 6:00 AM', temp: 50, condition: 'cloudy', precipitation: 10 },
            { time: '6:00 AM - 9:00 AM', temp: 55, condition: 'partlyCloudy', precipitation: 5 },
            { time: '9:00 AM - 12:00 PM', temp: 65, condition: 'sunny', precipitation: 0 },
            { time: '12:00 PM - 3:00 PM', temp: 73, condition: 'partlyCloudy', precipitation: 15 },
            { time: '3:00 PM - 6:00 PM', temp: 75, condition: 'cloudy', precipitation: 25 },
            { time: '6:00 PM - 9:00 PM', temp: 68, condition: 'rainy', precipitation: 40 },
            { time: '9:00 PM - 12:00 AM', temp: 60, condition: 'partlyCloudy', precipitation: 10 }
          ]
        };

        setForecast(sampleForecast);
        setLoading(false);
      }, 1500);
    } catch (error) {
      console.error('Error fetching weather data:', error);
      setError('Failed to fetch weather data. Please try again.');
      setLoading(false);
    }
  };

  const saveToPlaybook = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.log('❌ No token found');
        setNotification({
          show: true,
          title: 'Login Required',
          message: 'Please log in to save weather forecasts',
          type: 'warning',
          actionLabel: 'Log In',
          onAction: () => window.dispatchEvent(new Event('openAuthModal'))
        });
        setSaving(false);
        return;
      }
      
      if (!forecast) {
        console.log('❌ No forecast to save');
        setNotification({
          show: true,
          title: 'No Forecast to Save',
          message: 'Please search for weather forecast first before saving.',
          type: 'warning'
        });
        setSaving(false);
        return;
      }
      
      console.log('📤 Preparing to save forecast using apiService...');
      console.log('Location:', location);
      console.log('Wedding Date:', weddingDate);
      
      const requestData = {
        location,
        date: weddingDate,
        forecast,
        ai_recommendations: aiRecommendations,
        vendor_suggestions: vendorSuggestions,
        quick_checklist: quickChecklist
      };
      
      console.log('📦 Request data:', requestData);
      
      const response = await apiService.weather.saveForecast(requestData);
      
      console.log('✅ Save successful:', response.data);
      setSavedForecastId(response.data.forecast?.id || savedForecastId);
      setHasUnsavedChanges(false);
      setNotification({
        show: true,
        title: 'Saved Successfully! 🎉',
        message: savedForecastId 
          ? 'Weather forecast updated in your playbook' 
          : 'Weather forecast saved to your playbook',
        type: 'success'
      });
      
    } catch (error) {
      console.error('❌ Save failed with error:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      setNotification({
        show: true,
        title: 'Save Failed',
        message: `Could not save weather forecast: ${error.response?.data?.error || error.message}`,
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const formatDateDisplay = (dateString) => {
    // Parse the date string as local date to avoid timezone issues
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day); // month is 0-indexed
    
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const clearSearch = () => {
    setLocation('');
    setWeddingDate('');
    setForecast(null);
    setError(null);
  };

  const getConditionIcon = (condition) => {
    return weatherIcons[condition] || '🌤️';
  };

  const getRiskInfo = (level) => {
    return riskLevels[level] || riskLevels.medium;
  };  const pageIcons = [
    { emoji: '☀️', size: '6xl', opacity: '15' },
    { emoji: '☁️', size: '4xl', opacity: '15' },
    { emoji: '🌧️', size: '5xl', opacity: '15' },
    { emoji: '⛅', size: '3xl', opacity: '15' },
    { emoji: '🌈', size: '8xl', opacity: '15' },
    { emoji: '🌡️', size: '6xl', opacity: '15' }
  ];



  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
      <BackgroundIcons icons={pageIcons} />
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating sparkles */}
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-pulse opacity-10"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`
            }}
          >
            <span className="text-gold-400 text-lg">✨</span>
          </div>
        ))}
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12 relative z-10">
        
        {/* Elegant Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full shadow-2xl mb-8 animate-bounce">
            <div className="relative">
              <span className="text-2xl">☀️</span>
              <span className="absolute -top-1 -right-1 text-lg animate-pulse">☁️</span>
              <span className="absolute -bottom-1 -left-1 text-sm animate-pulse delay-500">🌧️</span>
            </div>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-gold-600 bg-clip-text text-transparent mb-6 tracking-wide">
            Weather Risk Advisor
          </h1>
          
          {/* Floral underline */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 text-gold-400">💖</div>
            </div>
          </div>
          
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed font-light">
            Get detailed weather insights and smart recommendations to ensure your perfect day stays perfect
          </p>
        </div>

        {/* Search Section */}
        <div className={`mb-12 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-gold-200/50 p-8">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-2xl flex items-center justify-center mr-4 shadow-lg animate-pulse">
                <span className="text-2xl">🌤️</span>
              </div>
              <h3 className="text-2xl font-serif font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                Weather Forecast
              </h3>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {/* Location Input */}
              <div className="relative">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-400">
                  <span className="text-xl">📍</span>
                </div>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Wedding location (city, state)"
                  className="w-full pl-12 pr-4 py-4 border-2 border-gold-200 rounded-xl focus:ring-4 focus:ring-gold-200 focus:border-gold-400 transition-all duration-300 bg-white/80 font-medium"
                />
              </div>

              {/* Date Input */}
              <div className="relative">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-rose-400">
                  <span className="text-xl">📅</span>
                </div>
                <input
                  type="date"
                  value={weddingDate}
                  onChange={(e) => setWeddingDate(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 border-2 border-gold-200 rounded-xl focus:ring-4 focus:ring-gold-200 focus:border-gold-400 transition-all duration-300 bg-white/80 font-medium"
                />
              </div>

              {/* Search Button */}
              <button
                onClick={searchWeather}
                disabled={loading || !location || !weddingDate}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-4 px-6 rounded-xl font-bold hover:from-blue-600 hover:to-cyan-600 hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Checking...
                  </div>
                ) : (
                  <>
                    <span className="mr-2">🔍</span>
                    Get Forecast
                  </>
                )}
              </button>
            </div>

            {error && (
              <div className="mt-6 bg-red-50 border-2 border-red-200 text-red-700 px-6 py-4 rounded-2xl shadow-lg">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">⚠️</span>
                  <span className="font-semibold">{error}</span>
                </div>
              </div>
            )}

            {(location || weddingDate) && (
              <div className="mt-6 text-center">
                <button
                  onClick={clearSearch}
                  className="px-6 py-2 border-2 border-gray-300 text-gray-600 rounded-xl font-medium hover:bg-gray-50 transition-all duration-300"
                >
                  Clear Search
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Forecast Results */}
        {forecast && (
          <div className={`space-y-8 transition-all duration-700 delay-400 ${forecast ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            
            {/* Main Forecast Card */}
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border-2 border-gold-200/50 p-8">
              <div className="grid md:grid-cols-2 gap-8">
                
                {/* Weather Overview */}
                <div>
                  <h4 className="text-2xl font-serif font-bold text-gray-800 mb-6">
                    {forecast.location} - {formatDateDisplay(forecast.date)}
                  </h4>

                  {/* Data Source Attribution */}
                  <div className="flex items-center justify-center mb-6">
                    <span className={`px-4 py-2 rounded-full text-sm font-bold border ${
                      forecast.api_used 
                        ? 'bg-green-100 text-green-700 border-green-200' 
                        : 'bg-yellow-100 text-yellow-700 border-yellow-200'
                    }`}>
                      {forecast.api_used 
                        ? `🌤️ Real Weather Data - ${apiStatus?.api_provider || 'OpenWeatherMap'} API` 
                        : '📊 Simulated Weather Data'
                      }
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-6 mb-6">
                    <div className="text-8xl">{getConditionIcon(forecast.conditions)}</div>
                    <div>
                      <div className="text-4xl font-bold text-gray-800">
                        {forecast.temperature.high}°F
                      </div>
                      <div className="text-xl text-gray-600">
                        Low: {forecast.temperature.low}°F
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-2xl">💧</span>
                        <span className="font-semibold text-gray-700">Humidity</span>
                      </div>
                      <div className="text-2xl font-bold text-blue-600">{forecast.humidity}%</div>
                    </div>
                    
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-2xl">💨</span>
                        <span className="font-semibold text-gray-700">Wind</span>
                      </div>
                      <div className="text-2xl font-bold text-green-600">{forecast.windSpeed} mph</div>
                    </div>
                    
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-2xl">🌧️</span>
                        <span className="font-semibold text-gray-700">Rain Chance</span>
                      </div>
                      <div className="text-2xl font-bold text-purple-600">{forecast.precipitation}%</div>
                    </div>
                    
                    <div className={`bg-gradient-to-r ${getRiskInfo(forecast.riskLevel).color} rounded-xl p-4 border border-gray-200`}>
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-2xl">{getRiskInfo(forecast.riskLevel).icon}</span>
                        <span className="font-semibold text-gray-700">Risk Level</span>
                      </div>
                      <div className={`text-2xl font-bold capitalize ${getRiskInfo(forecast.riskLevel).textColor}`}>
                        {forecast.riskLevel}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Hourly Forecast */}
                <div>
                  <h5 className="text-xl font-bold text-gray-800 mb-4">Hourly Forecast</h5>
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {forecast.hourlyForecast.map((hour, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-gradient-to-r from-white/60 to-rose-50/60 rounded-xl p-4 border border-gold-200/50 animate-fadeInScale"
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <div className="flex items-center space-x-4">
                          <span className="text-2xl">{getConditionIcon(hour.condition)}</span>
                          <div>
                            <div className="font-bold text-gray-800">{hour.time}</div>
                            <div className="text-sm text-gray-600">{hour.temp}°F</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-blue-600 font-semibold">
                            {hour.precipitation}% rain
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Weather Preparations Card */}
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border-2 border-gold-200/50 p-8">
              <div className="flex items-center mb-8">
                <div className="w-14 h-14 bg-gradient-to-r from-purple-500 via-pink-500 to-gold-500 rounded-2xl flex items-center justify-center mr-4 shadow-lg animate-pulse">
                  <span className="text-3xl">💡</span>
                </div>
                <div>
                  <h4 className="text-3xl font-serif font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-gold-600 bg-clip-text text-transparent">
                    Weather Preparations
                  </h4>
                  <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full mt-2"></div>
                </div>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {/* Weather-Specific Recommendations */}
                <div className="md:col-span-2">
                  <div className="flex items-center mb-4">
                    <span className="text-2xl mr-2">🌤️</span>
                    <h5 className="text-xl font-bold text-gray-800">Weather-Specific Actions</h5>
                  </div>
                  <div className="space-y-3">
                    {forecast.recommendations.map((rec, index) => (
                      <div
                        key={index}
                        className="group flex items-start space-x-3 p-4 bg-gradient-to-r from-rose-50/80 via-pink-50/60 to-champagne-50/80 rounded-xl border border-rose-200/50 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 animate-fadeInScale"
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-rose-400 to-pink-400 rounded-full flex items-center justify-center text-white font-bold shadow-md group-hover:scale-110 transition-transform">
                          {index + 1}
                        </div>
                        <span className="text-gray-700 leading-relaxed flex-1">{rec}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Quick Action Checklist */}
                <div>
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-purple-400 to-pink-400 rounded-xl flex items-center justify-center mr-3 shadow-md">
                      <span className="text-2xl">✅</span>
                    </div>
                    <h5 className="text-xl font-serif font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                      Quick Checklist
                    </h5>
                  </div>
                  <div className="space-y-2">
                    {(quickChecklist.length > 0 ? quickChecklist : [
                      'Check venue backup plan',
                      'Confirm vendor availability',
                      'Review insurance coverage',
                      'Prepare guest communication',
                      'Update timeline if needed',
                      'Stock emergency supplies'
                    ]).map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-3 p-3 bg-gradient-to-r from-purple-50/80 to-pink-50/80 rounded-xl border border-purple-200/50 hover:shadow-lg hover:scale-[1.02] transition-all duration-300"
                        style={{ animationDelay: `${index * 80}ms` }}
                      >
                        <div className="w-6 h-6 border-2 border-purple-400 rounded-lg flex items-center justify-center flex-shrink-0 bg-white shadow-sm">
                          <span className="text-purple-600 text-sm font-bold">✓</span>
                        </div>
                        <span className="text-sm text-gray-700 font-medium">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Emergency Contacts & Resources */}
              <div className="border-t-2 border-gold-200/50 pt-8">
                <div className="flex items-center mb-6">
                  <span className="text-2xl mr-2">📞</span>
                  <h5 className="text-xl font-bold text-gray-800">Emergency Contacts & Resources</h5>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="group p-5 bg-gradient-to-br from-blue-50/80 to-cyan-50/80 rounded-xl border border-blue-200/50 hover:shadow-xl hover:scale-105 transition-all duration-300">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-lg flex items-center justify-center mr-3 shadow-md group-hover:rotate-12 transition-transform">
                        <span className="text-xl">🏛️</span>
                      </div>
                      <div className="font-bold text-gray-800 text-lg">Venue Team</div>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">Primary contact for indoor backup options and facility adjustments</p>
                    <div className="text-xs text-blue-600 font-semibold">Call 24-48 hours before</div>
                  </div>
                  
                  <div className="group p-5 bg-gradient-to-br from-green-50/80 to-emerald-50/80 rounded-xl border border-green-200/50 hover:shadow-xl hover:scale-105 transition-all duration-300">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-green-400 to-emerald-400 rounded-lg flex items-center justify-center mr-3 shadow-md group-hover:rotate-12 transition-transform">
                        <span className="text-xl">⛺</span>
                      </div>
                      <div className="font-bold text-gray-800 text-lg">Rental Services</div>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">Tents, heaters, fans, and weather protection equipment</p>
                    <div className="text-xs text-green-600 font-semibold">Book 1-2 weeks ahead</div>
                  </div>
                  
                  <div className="group p-5 bg-gradient-to-br from-purple-50/80 to-pink-50/80 rounded-xl border border-purple-200/50 hover:shadow-xl hover:scale-105 transition-all duration-300">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-purple-400 to-pink-400 rounded-lg flex items-center justify-center mr-3 shadow-md group-hover:rotate-12 transition-transform">
                        <span className="text-xl">🚗</span>
                      </div>
                      <div className="font-bold text-gray-800 text-lg">Transportation</div>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">Weather-appropriate vehicles and shuttle services</p>
                    <div className="text-xs text-purple-600 font-semibold">Confirm 1 week before</div>
                  </div>
                </div>
              </div>

              {/* Weather Wisdom Tips */}
              <div className="mt-8 p-6 bg-gradient-to-r from-gold-50/80 via-champagne-50/80 to-rose-50/80 rounded-2xl border-2 border-gold-200/50">
                {/* VowAI Weather Wisdom */}
                <div className="relative bg-gradient-to-br from-gold-50 via-champagne-50 to-rose-50 rounded-2xl p-6 border-2 border-gold-200/50 shadow-xl overflow-hidden">
                  {/* Decorative background */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-gold-200/20 to-rose-200/20 rounded-full blur-2xl"></div>
                  
                  <div className="flex items-start relative z-10">
                    <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-r from-gold-400 via-champagne-400 to-rose-400 rounded-2xl flex items-center justify-center mr-4 shadow-lg animate-pulse">
                      <span className="text-3xl">💫</span>
                    </div>
                    <div>
                      <h6 className="font-serif font-bold text-xl mb-3">
                        <span className="bg-gradient-to-r from-gold-600 via-champagne-600 to-rose-600 bg-clip-text text-transparent">
                          VowAI Weather Wisdom
                        </span>
                      </h6>
                      <p className="text-gray-700 leading-relaxed text-base">
                        Remember: Some of the most magical wedding moments happen when plans adapt to nature's surprises. 
                        Having a solid backup plan doesn't mean expecting the worst - it means being prepared to embrace whatever 
                        your special day brings with grace and joy. Rain on your wedding day? Many cultures consider it a blessing! ✨
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Smart Vendor Matching CTA */}
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border-2 border-purple-200/50 p-10 relative overflow-hidden">
              {/* Decorative background elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-100/30 to-pink-100/30 rounded-full blur-3xl -z-10"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-gold-100/30 to-champagne-100/30 rounded-full blur-3xl -z-10"></div>
              
              <div className="text-center max-w-3xl mx-auto">
                {/* Icon */}
                <div className="w-20 h-20 bg-gradient-to-r from-purple-500 via-pink-500 to-gold-500 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-2xl animate-pulse">
                  <span className="text-4xl">🎯</span>
                </div>
                
                {/* Title */}
                <h4 className="text-3xl font-serif font-bold mb-4">
                  <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-gold-600 bg-clip-text text-transparent">
                    Need Weather-Ready Vendors?
                  </span>
                </h4>
                
                {/* Description */}
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Our <span className="font-bold text-purple-600">Smart Vendor Matching</span> uses advanced AI to find the perfect vendors who can handle any weather scenario. Get personalized recommendations based on your wedding theme, budget, and specific weather concerns.
                </p>
                
                {/* Features */}
                <div className="grid md:grid-cols-3 gap-4 mb-8">
                  <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200/50">
                    <div className="text-2xl mb-2">🤖</div>
                    <div className="font-bold text-gray-800 mb-1">AI-Powered Matching</div>
                    <div className="text-sm text-gray-600">Smart vendor recommendations</div>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl border border-pink-200/50">
                    <div className="text-2xl mb-2">⛈️</div>
                    <div className="font-bold text-gray-800 mb-1">Weather-Ready</div>
                    <div className="text-sm text-gray-600">Vendors with backup plans</div>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-gold-50 to-champagne-50 rounded-xl border border-gold-200/50">
                    <div className="text-2xl mb-2">💎</div>
                    <div className="font-bold text-gray-800 mb-1">Curated Selection</div>
                    <div className="text-sm text-gray-600">Premium vendor network</div>
                  </div>
                </div>
                
                {/* CTA Button */}
                <a
                  href="/vendor-vault/smart-vendor-matching"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-gradient-to-r from-purple-600 via-pink-600 to-gold-600 text-white px-10 py-4 rounded-xl font-bold text-lg hover:from-purple-700 hover:via-pink-700 hover:to-gold-700 hover:shadow-2xl transform hover:scale-105 transition-all duration-300 group"
                >
                  <span className="mr-3">🎯</span>
                  Explore Smart Vendor Matching
                  <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </a>
                
                <p className="text-sm text-gray-500 mt-4">
                  Opens in new tab • Free to explore
                </p>
              </div>
            </div>

            {/* Save to Playbook Section */}
            <div className="bg-gradient-to-br from-white/90 to-gold-50/80 backdrop-blur-sm rounded-3xl shadow-2xl border-2 border-gold-300/50 p-10 relative overflow-hidden">
              {/* Decorative background */}
              <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-gold-200/20 to-rose-200/20 rounded-full blur-3xl"></div>
              
              <div className="text-center relative z-10">
                <button
                  onClick={saveToPlaybook}
                  disabled={saving || hasUnsavedChanges === false}
                  className={`w-full bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-4 px-6 rounded-xl font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${hasUnsavedChanges && !saving ? 'animate-pulse' : ''}`}
                >
                  {saving ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      <span>Saving to Playbook...</span>
                    </div>
                  ) : hasUnsavedChanges === false ? (
                    <div className="flex items-center justify-center">
                      <span className="mr-2">✅</span>
                      <span>Saved to Playbook</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <span className="mr-2">📚</span>
                      <span>Save to Playbook</span>
                    </div>
                  )}
                </button>
                <p className="text-sm text-gray-600 mt-3">
                  {hasUnsavedChanges === false 
                    ? 'Your forecast is saved and ready in your playbook' 
                    : 'Save this forecast with AI recommendations for future reference'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Notification Modal */}
        <NotificationModal
          isOpen={notification.show}
          onClose={() => setNotification({ show: false, title: '', message: '', type: '' })}
          type={notification.type}
      actionLabel={notification.actionLabel}
      onAction={notification.onAction}
          title={notification.title}
          message={notification.message}
        />

        {/* Luxury Footer */}
        <div className={`mt-16 text-center transition-all duration-700 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="w-32 h-px bg-gradient-to-r from-transparent via-gold-400 to-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-light italic">
            Part of your personalized VowAI Wedding Journey
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        .animate-fadeInScale {
          animation: fadeInScale 0.6s ease-out;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        .delay-200 { animation-delay: 200ms; }
        .delay-400 { animation-delay: 400ms; }
        .delay-500 { animation-delay: 500ms; }
        .delay-600 { animation-delay: 600ms; }
        .delay-1000 { animation-delay: 1000ms; }
        .delay-1500 { animation-delay: 1500ms; }
        .delay-2000 { animation-delay: 2000ms; }
      `}</style>
    </div>
  );
};

export default WeatherRiskAdvisor;
